import maplibregl, { type GeoJSONSource, type ImageSource, type Map as MapLibreMap, type RasterTileSource } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { darkBasemapStyle } from "./basemap";
import { BELGIUM_CENTER, EUROPE_HOME_BBOX, IBERIA_CENTER, clickRadiusM, heatFrameBounds, nearestByHaversine, paddedHeatOutline } from "../shared/geo";
import { heatColorExpression, heatFeatures, heatLonLatAtPlayhead, type HeatPoint } from "../shared/heat";
import { eventClassOf } from "../shared/event-state";
import { incidentVisibleAtPlayhead } from "../shared/status";
import { effisGwisTileUrl, utcIsoDate } from "../shared/sources";
import type { FiresResponse, Incident } from "../shared/types";
import { detectionAgeOpacity } from "./format";
import { installWindLayer, type WindLayerHandle } from "./wind-layer";

export const PLACES = {
  be: { center: BELGIUM_CENTER, zoom: 7.2 },
  ib: { center: IBERIA_CENTER, zoom: 5.4 },
  // Alps / north Italy — matches EUROPE_HOME_BBOX, not the empty 72°N ingest frame.
  eu: { center: [9.5, 45.4] as [number, number], zoom: 4.15 },
};

export function createMap(container: HTMLElement): MapLibreMap {
  const map = new maplibregl.Map({
    container,
    style: darkBasemapStyle(),
    // Continent first. Locate me is a button — we do not ask on load.
    center: PLACES.eu.center,
    zoom: PLACES.eu.zoom,
    maxZoom: 12,
    minZoom: 3,
    // Credits live in the footer / mobile i — MapLibre’s white “i” was a stray blob.
    attributionControl: false,
  });
  map.on("load", () => installLayers(map));
  return map;
}

/** Same FRP stops up close; continent zoom only caps the huge end so rings stay visible. */
function frpRadiusByZoom(farStops: number[], nearStops: number[]): maplibregl.ExpressionSpecification {
  return [
    "interpolate",
    ["linear"],
    ["zoom"],
    3,
    ["interpolate", ["linear"], ["get", "frp"], ...farStops],
    8,
    ["interpolate", ["linear"], ["get", "frp"], ...nearStops],
  ];
}

function installLayers(map: MapLibreMap): void {
  const fwiTime = utcIsoDate(0);
  map.addSource("detections", { type: "geojson", data: emptyFc() });
  map.addSource("incidents", { type: "geojson", data: emptyFc() });
  map.addSource("selection", { type: "geojson", data: emptyFc() });
  // EFFIS CORS is open — these tiles never enter the Worker, so they don't eat CF quota.
  map.addSource("fwi", {
    type: "raster",
    tiles: [effisGwisTileUrl("ecmwf.fwi", fwiTime)],
    tileSize: 256,
    attribution: "FWI: Copernicus EFFIS / ECMWF",
  });
  map.addSource("ba", {
    type: "raster",
    tiles: [effisGwisTileUrl("nrt.ba.week")],
    tileSize: 256,
    attribution: "Burned: Copernicus EFFIS RDA",
  });
  map.addSource("risk", {
    type: "raster",
    tiles: ["/api/tiles/risk/{z}/{x}/{y}.png"],
    tileSize: 256,
  });
  map.addSource("lst", {
    type: "raster",
    tiles: ["/api/tiles/lst/{z}/{x}/{y}.png"],
    tileSize: 256,
  });
  // 10-minute MTG FCI GeoColour (EUMETView). Photograph, not a fire outline.
  map.addSource("sat", {
    type: "raster",
    tiles: ["/api/tiles/mtg-geo/{z}/{x}/{y}.png"],
    tileSize: 256,
    attribution: "GeoColour: EUMETSAT MTG FCI",
  });
  // LSA SAF ADAGUC needs the Worker proxy (CORS). Overlay only — not clustered.
  map.addSource("mtg", {
    type: "raster",
    tiles: ["/api/tiles/mtg-frp/{z}/{x}/{y}.png"],
    tileSize: 256,
    attribution: "MTG FRP: LSA SAF (demonstration)",
  });

  map.addLayer({
    id: "fwi",
    type: "raster",
    source: "fwi",
    layout: { visibility: "none" },
    paint: { "raster-opacity": 0.55 },
  });
  map.addLayer({
    id: "risk",
    type: "raster",
    source: "risk",
    layout: { visibility: "none" },
    paint: { "raster-opacity": 0.55 },
  });
  map.addLayer({
    id: "ba",
    type: "raster",
    source: "ba",
    layout: { visibility: "none" },
    // EFFIS default is a bright red fill. Dim it so it does not read as fire.
    paint: { "raster-opacity": 0.42, "raster-brightness-max": 0.38, "raster-saturation": -0.45 },
  });
  // Same EFFIS RDA product as Burned, but one GetMap around the open fire.
  // Independent of the Burned button. Sits under heat so the glow still reads.
  map.addSource("ba-event", {
    type: "image",
    url: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
    coordinates: [
      [0, 1],
      [1, 1],
      [1, 0],
      [0, 0],
    ],
  });
  map.addLayer({
    id: "ba-event",
    type: "raster",
    source: "ba-event",
    layout: { visibility: "none" },
    paint: { "raster-opacity": 0.82 },
  });
  map.addLayer({
    id: "lst",
    type: "raster",
    source: "lst",
    layout: { visibility: "none" },
    paint: { "raster-opacity": 0.62 },
  });
  map.addLayer({
    id: "sat",
    type: "raster",
    source: "sat",
    layout: { visibility: "none" },
    paint: { "raster-opacity": 0.88 },
  });
  // One JPEG around a selected fire. Play never swaps the Europe XYZ sat tiles.
  map.addSource("sat-event", {
    type: "image",
    url: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
    coordinates: [
      [0, 1],
      [1, 1],
      [1, 0],
      [0, 0],
    ],
  });
  map.addLayer({
    id: "sat-event",
    type: "raster",
    source: "sat-event",
    layout: { visibility: "none" },
    paint: { "raster-opacity": 0.9 },
  });

  map.addLayer({
    id: "heat",
    type: "heatmap",
    source: "detections",
    paint: {
      // Weight already includes FRP × age-from-playhead. Radius grows with zoom
      // so 375 m VIIRS and ~1 km MTG/MODIS footprints merge instead of a grid.
      "heatmap-weight": ["get", "weight"],
      "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 3, 0.55, 8, 1.4, 12, 1.9],
      "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 3, 10, 8, 36, 11, 52, 13, 76],
      "heatmap-opacity": 0.72,
      "heatmap-color": heatColorExpression() as maplibregl.ExpressionSpecification,
    },
  });
  // Custom layer stacks under heat — addLayer(..., "heat") inserts before that id.
  windHandle = installWindLayer(map);

  map.addLayer({
    id: "mtg",
    type: "raster",
    source: "mtg",
    layout: { visibility: "visible" },
    paint: { "raster-opacity": 0.92 },
  });

  // Invisible fat target so a heat blob is clickable even when the centroid dot is tiny.
  map.addLayer({
    id: "incident-hit",
    type: "circle",
    source: "incidents",
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 14, 8, 22, 11, 28],
      "circle-color": "#000",
      "circle-opacity": 0,
    },
  });

  map.addLayer({
    id: "incident-halo",
    type: "circle",
    source: "incidents",
    paint: {
      // Floor stays outside the heat glow; only the 250 MW end is capped when zoomed out.
      "circle-radius": frpRadiusByZoom([0, 10, 20, 11, 80, 13, 250, 15], [0, 9, 20, 12, 80, 18, 250, 28]),
      "circle-color": "transparent",
      // Stroke = event class (how sure). Width = fire_probability (was a fake 70).
      "circle-stroke-color": [
        "match",
        ["get", "event_class"],
        "suspected",
        "#f5c542",
        "likely",
        "#ff8a1a",
        "observed",
        "#ff4d3a",
        "#ff6a1a",
      ],
      // 1px vanished inside the heatmap — 1.8px is the clickable ring at every size.
      "circle-stroke-width": [
        "case",
        ["==", ["get", "sel"], 1],
        4,
        ["interpolate", ["linear"], ["get", "confidence"], 30, 1.8, 90, 3],
      ],
      "circle-stroke-opacity": ["case", ["==", ["get", "sel"], 1], 0, 0.7],
      "circle-opacity": 0,
    },
  });

  map.addLayer({
    id: "incident-dot",
    type: "circle",
    source: "incidents",
    paint: {
      "circle-radius": [
        "case",
        ["==", ["get", "sel"], 1],
        frpRadiusByZoom([0, 6, 15, 8, 80, 10, 250, 11], [0, 7, 15, 9, 80, 12, 250, 17]),
        frpRadiusByZoom([0, 4, 15, 5.5, 80, 7, 250, 8], [0, 4.5, 15, 6, 80, 9, 250, 14]),
      ],
      "circle-color": [
        "match",
        ["get", "event_class"],
        "suspected",
        "#f5c542",
        "likely",
        "#ff8a1a",
        "observed",
        "#ff4d3a",
        "#ff6a1a",
      ],
      "circle-opacity": ["case", ["==", ["get", "sel"], 1], 0, ["get", "opacity"]],
    },
  });
  // Cream stroke around the selected glow — sits above heat/overlays.
  map.addLayer({
    id: "selection-outline",
    type: "line",
    source: "selection",
    paint: {
      "line-color": "#ffe6c0",
      "line-width": 2.2,
      "line-opacity": 0.92,
      "line-blur": 0.35,
    },
    layout: { "line-join": "round", "line-cap": "round" },
  });
}

let lastEventSatKey = "";
let lastEventBurnedKey = "";
let windHandle: WindLayerHandle | null = null;

/** Local GeoColour photo on the selected fire. Null hides it — never a continent replay. */
export function setEventSat(
  map: MapLibreMap,
  frame: {
    url: string;
    coordinates: [[number, number], [number, number], [number, number], [number, number]];
  } | null,
): void {
  const src = map.getSource("sat-event") as ImageSource | undefined;
  if (!src || !map.getLayer("sat-event")) return;
  if (!frame) {
    lastEventSatKey = "";
    map.setLayoutProperty("sat-event", "visibility", "none");
    return;
  }
  const key = `${frame.url}|${frame.coordinates[0][0].toFixed(3)}|${frame.coordinates[0][1].toFixed(3)}`;
  if (key !== lastEventSatKey) {
    lastEventSatKey = key;
    src.updateImage({ url: frame.url, coordinates: frame.coordinates });
  }
  map.setLayoutProperty("sat-event", "visibility", "visible");
}

/** Local EFFIS scar around the selected fire. Null hides it — never Europe-wide tiles. */
export function setEventBurned(
  map: MapLibreMap,
  frame: {
    url: string;
    coordinates: [[number, number], [number, number], [number, number], [number, number]];
  } | null,
): void {
  const src = map.getSource("ba-event") as ImageSource | undefined;
  if (!src || !map.getLayer("ba-event")) return;
  if (!frame) {
    lastEventBurnedKey = "";
    map.setLayoutProperty("ba-event", "visibility", "none");
    return;
  }
  const key = `${frame.url}|${frame.coordinates[0][0].toFixed(3)}|${frame.coordinates[0][1].toFixed(3)}`;
  if (key !== lastEventBurnedKey) {
    lastEventBurnedKey = key;
    src.updateImage({ url: frame.url, coordinates: frame.coordinates });
  }
  map.setLayoutProperty("ba-event", "visibility", "visible");
}

/** Swap ECMWF FWI tiles to another forecast day. Browser → EFFIS, not the Worker. */
export function setFwiTime(map: MapLibreMap, isoDate: string): void {
  const src = map.getSource("fwi");
  if (!src || src.type !== "raster") return;
  (src as RasterTileSource).setTiles([effisGwisTileUrl("ecmwf.fwi", isoDate)]);
}

export function setLayerVisible(map: MapLibreMap, id: string, on: boolean): void {
  if (id === "wind") {
    windHandle?.setEnabled(on);
    return;
  }
  const vis = on ? "visible" : "none";
  if (id === "fire") {
    for (const layer of ["heat", "selection-outline", "incident-hit", "incident-halo", "incident-dot", "mtg"]) {
      if (map.getLayer(layer)) map.setLayoutProperty(layer, "visibility", vis);
    }
    return;
  }
  if (!map.getLayer(id)) return;
  map.setLayoutProperty(id, "visibility", vis);
}

export function applyData(
  map: MapLibreMap,
  data: FiresResponse,
  playheadMs: number,
  selectedId: string | null = null,
  // Pre-parsed on /api/fires load so Play does not Date.parse every point every frame.
  heat: HeatPoint[] = [],
): void {
  const detSource = map.getSource("detections") as GeoJSONSource | undefined;
  const incSource = map.getSource("incidents") as GeoJSONSource | undefined;
  const selSource = map.getSource("selection") as GeoJSONSource | undefined;
  if (!detSource || !incSource) return;

  detSource.setData({
    type: "FeatureCollection",
    features: heatFeatures(heat, playheadMs),
  });

  incSource.setData({
    type: "FeatureCollection",
    features: data.incidents
      .filter((i) => incidentVisibleAtPlayhead(i.first_seen, playheadMs))
      .map((i) => incidentFeature(i, playheadMs, selectedId)),
  });

  if (selSource) {
    const ring = selectedHeatRing(heat, data.incidents, selectedId, playheadMs);
    selSource.setData({
      type: "FeatureCollection",
      features: ring
        ? [
            {
              type: "Feature",
              geometry: { type: "Polygon", coordinates: [ring] },
              properties: {},
            },
          ]
        : [],
    });
  }
}

/** Close enough that 375 m VIIRS cells read as a trail, not one county glow. */
export const EVENT_HEAT_ZOOM = 11.2;

/** Tight enough that the 28 km GeoColour frame fills the view during Play. */
export const EVENT_SAT_ZOOM = 10.6;

export function flyToIncident(map: MapLibreMap, incident: Incident, zoom = EVENT_HEAT_ZOOM): void {
  map.flyTo({ center: [incident.lon, incident.lat], zoom: Math.max(map.getZoom(), zoom), speed: 0.9 });
}

/** Heat pixels for one fire — the full trail, not the playhead-faded ring. */
export function incidentHeatPoints(heat: HeatPoint[], incidentId: string): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  for (const p of heat) {
    if (p.incidentId === incidentId) pts.push([p.lon, p.lat]);
  }
  return pts;
}

/** Frame the fire so playback can show cells appearing. Large scars zoom out to fit. */
export function flyToIncidentHeat(
  map: MapLibreMap,
  incident: Incident,
  heatPts: Array<[number, number]>,
  opts?: { immediate?: boolean },
): void {
  const bounds = heatFrameBounds(heatPts, [incident.lon, incident.lat]);
  if (!bounds) {
    flyToIncident(map, incident);
    return;
  }
  const wide = typeof window !== "undefined" && window.innerWidth > 720;
  const panelOpen = typeof document !== "undefined" && document.body.classList.contains("incident-open");
  map.fitBounds(bounds, {
    // Keep the glow out from under the sidebar and the time footer.
    padding: wide && panelOpen
      ? { top: 168, bottom: 168, left: 20, right: 400 }
      : { top: 168, bottom: 168, left: 16, right: 16 },
    maxZoom: EVENT_HEAT_ZOOM,
    duration: opts?.immediate ? 0 : 900,
  });
}

/** Locate me — only after a click. Denied / missing GPS leaves the camera. */
export function locateHere(map: MapLibreMap, btn?: HTMLElement): void {
  if (!navigator.geolocation) {
    if (btn) btn.dataset.tip = "This browser cannot share a location.";
    return;
  }
  btn?.classList.add("is-busy");
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      btn?.classList.remove("is-busy", "is-err");
      if (btn) btn.dataset.tip = "Zoom to where you are. Stays in this browser.";
      map.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 8.2, speed: 0.9 });
    },
    () => {
      btn?.classList.remove("is-busy");
      btn?.classList.add("is-err");
      if (btn) btn.dataset.tip = "Location was blocked. Allow it for this site in the address bar, then try again.";
    },
    { enableHighAccuracy: false, timeout: 15_000, maximumAge: 0 },
  );
}

/** Fit the home camera — activity belt, not the tall ingest rectangle. */
export function fitEurope(map: MapLibreMap, duration = 800): void {
  map.fitBounds(
    [
      [EUROPE_HOME_BBOX.west, EUROPE_HOME_BBOX.south],
      [EUROPE_HOME_BBOX.east, EUROPE_HOME_BBOX.north],
    ],
    { padding: 48, duration, maxZoom: 4.4 },
  );
}

/** Hit the fat target, then heatmap pixels, then nearest incident in the current window. */
export function pickIncidentId(
  map: MapLibreMap,
  point: { x: number; y: number },
  lngLat: { lng: number; lat: number },
  incidents: Incident[],
): string | null {
  const pad = 18;
  const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
    [point.x - pad, point.y - pad],
    [point.x + pad, point.y + pad],
  ];
  const layers = ["incident-hit", "incident-dot", "incident-halo"].filter((id) => map.getLayer(id));
  const hits = map.queryRenderedFeatures(bbox, { layers });
  const id = hits[0]?.properties?.id as string | undefined;
  if (id) return id;
  if (map.getLayer("heat")) {
    const heatHits = map.queryRenderedFeatures(bbox, { layers: ["heat"] });
    const hid = heatHits[0]?.properties?.incident_id as string | undefined;
    if (hid) return hid;
  }
  return nearestByHaversine(incidents, lngLat.lat, lngLat.lng, clickRadiusM(map.getZoom()))?.id ?? null;
}

/** Visible heat cells for this fire, or a circle on the incident if the trail has faded. */
function selectedHeatRing(
  heat: HeatPoint[],
  incidents: Incident[],
  selectedId: string | null,
  playheadMs: number,
): Array<[number, number]> | null {
  if (!selectedId) return null;
  const pts = heatLonLatAtPlayhead(heat, playheadMs, selectedId);
  if (pts.length === 0) {
    const inc = incidents.find((i) => i.id === selectedId);
    if (!inc) return null;
    pts.push([inc.lon, inc.lat]);
  }
  const ring = paddedHeatOutline(pts);
  return ring.length >= 4 ? ring : null;
}

function incidentFeature(i: Incident, playheadMs: number, selectedId: string | null) {
  return {
    type: "Feature" as const,
    geometry: { type: "Point" as const, coordinates: [i.lon, i.lat] },
    properties: {
      id: i.id,
      frp: i.frp_mw,
      status: i.status,
      label: i.label,
      event_class: eventClassOf(i.event_status),
      // Halo width already interpolates on this 0–100 scale.
      confidence: Math.round((i.fire_probability ?? 0.7) * 100),
      opacity: detectionAgeOpacity(i.last_seen, playheadMs),
      sel: i.id === selectedId ? 1 : 0,
    },
  };
}

function emptyFc() {
  return { type: "FeatureCollection" as const, features: [] };
}
